import { Component, DOCUMENT, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { AffordabilityService, FormFieldsResiService } from '../../services';
import { LghfAnalyticsService } from '../../services/lghf-analytics.service';
import { ModalService } from 'apps/lghf/src/app/services/modal.service';
import { PersonalDetailsComponent } from '../personal-details/personal-details.component';
import { Subscription } from 'rxjs';
import { Channel } from '../../models/channels';

const inputMargin = 35;

@Component({
  templateUrl: 'affordability-form.component.html',
  selector: 'app-affordability-form',
  styleUrls: ['./affordability-form.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, FormlyModule],
})
export class AffordabilityFormComponent implements OnInit, OnDestroy {
  public readonly affordabilityService = inject(AffordabilityService);
  public readonly lghfAnalyticsService = inject(LghfAnalyticsService);
  private readonly formFieldsResiService = inject(FormFieldsResiService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly modalService = inject(ModalService);
  private readonly document = inject<Document>(DOCUMENT);

  public fields: FormlyFieldConfig[] = [];
  public iFrameOffsetTop: number = 0;
  @Input() public print: boolean = false;

  private personalDetailsModalClosedSubscription: Subscription;

  public constructor() {
    const lghfAnalyticsService = this.lghfAnalyticsService;

    lghfAnalyticsService.callToAction('form', this.lendingType);
  }

  public get lendingType() {
    return this.activatedRoute.snapshot.params['lendingType'] ?? ('resi' as 'resi' | 'btl');
  }

  public get channel(): Channel {
    return this.activatedRoute.snapshot.queryParams['channel'] ?? Channel.Intermediary;
  }

  public get affiliateCID() {
    return this.activatedRoute.snapshot.queryParams['affiliateCID'] ?? '';
  }

  public get model() {
    return this.affordabilityService.model;
  }

  public get trafficSource() {
    return this.activatedRoute.snapshot.queryParams['trafficSource'] ?? '';
  }

  public get isCustomerChannel(): boolean {
    return this.channel === Channel.Customer;
  }

  public ngOnInit() {
    try {
      this.fields = this.formFieldsResiService.fields;
      if (!this.affordabilityService.edit && !this.affordabilityService.newSearch && !this.print) {
        this.affordabilityService.form = new UntypedFormGroup({});
        this.affordabilityService.resetModel();
      }
      this.affordabilityService.edit = false;
      this.affordabilityService.newSearch = false;
    } catch {
      this.router.navigate(['']);
    }
  }

  public ngOnDestroy(): void {
    this.personalDetailsModalClosedSubscription?.unsubscribe();
  }

  public submit() {
    if (!this.validateForm()) {
      return;
    }

    if (this.isCustomerChannel) {
      this.openPersonalDetailsModal();
    } else {
      this.getResults();
    }
  }

  private openPersonalDetailsModal(): void {
    if (this.affordabilityService.hasPersonalDetails) {
      this.getResults();
      return;
    }

    const applicantsCount = this.affordabilityService.model.applicants?.numberOfApplicants ?? 1;
    this.personalDetailsModalClosedSubscription = this.modalService
      .open(
        {
          applicantsCount,
        },
        {
          template: PersonalDetailsComponent,
          modalDialogClass: 'modal-lg',
        },
      )
      .closed.subscribe(() => {
        this.getResults();
      });
  }

  private validateForm(): boolean {
    Object.keys(this.affordabilityService.form.controls).forEach((key) => {
      this.affordabilityService.form.controls[key].markAsDirty();
    });

    this.affordabilityService.form.markAllAsTouched();

    if (!this.affordabilityService.form.valid) {
      this.scrollToFirstInvalidField();
      return false;
    }

    return true;
  }

  private getResults() {
    if ('parentIFrame' in window) {
      (window['parentIFrame'] as any).getPageInfo((info) => {
        this.iFrameOffsetTop = info.offsetTop;
      });
      (window['parentIFrame'] as any).getPageInfo(false);
      setTimeout(() => {
        window['parentIFrame'].scrollTo(0, this.iFrameOffsetTop);
      }, 200);
    } else {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 200);
    }

    this.affordabilityService.getResults(this.lendingType, this.affiliateCID, this.trafficSource, this.channel);
    this.router.navigate(['results'], {
      queryParams: {
        lendingType: this.lendingType,
        channel: this.channel,
        affiliateCID: this.affiliateCID,
        trafficSource: this.trafficSource,
      },
    });
  }

  private scrollToFirstInvalidField() {
    if ('parentIFrame' in window) {
      (window['parentIFrame'] as any).getPageInfo((info) => {
        this.iFrameOffsetTop = info.offsetTop;
      });
      (window['parentIFrame'] as any).getPageInfo(false);
    }
    setTimeout(() => {
      const invalidDiv = Array.prototype.slice
        .call(document.querySelectorAll('formly-field .invalid-feedback'))
        .filter((e: HTMLElement) => window.getComputedStyle(e.closest('formly-field')).display !== 'none')
        .filter((e: HTMLElement) => e.offsetParent && e.querySelector('formly-validation-message').textContent);

      if (invalidDiv && invalidDiv.length > 0) {
        const formlyField = invalidDiv[0].closest('formly-field');
        let invalidInput = formlyField.querySelector('input') || formlyField.querySelector('.form-control.ng-invalid');
        // Custom radio
        if (invalidInput?.tagName === 'DIV') {
          invalidInput = invalidInput.querySelector('input');
        }

        if (invalidInput) {
          invalidInput.focus();
          const invalidElementTop = invalidInput.getBoundingClientRect().top;
          if (invalidElementTop > 0) {
            if ('parentIFrame' in window) {
              const scrollTop = invalidElementTop - inputMargin + this.iFrameOffsetTop;
              (window['parentIFrame'] as any).scrollTo(0, scrollTop);
            } else {
              const scrollTop = this.document.documentElement.scrollTop + invalidElementTop - inputMargin;
              window.scrollTo({ top: scrollTop, behavior: 'smooth' });
            }
          }
        }
      }
    }, 200);
  }
}
